import { Fragment, useMemo, useState, useCallback, useEffect } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { Menu, Transition } from "@headlessui/react";
import { isMobile } from "react-device-detect";
import {
    DocumentDuplicateIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";

import { useSolanaWalletModal } from "../Contexts/useSolanaWalletModal";
import { useAuth } from "../Contexts/useAuth";

import { Toastify } from "./Toastify";

const TOASTIFY_DELAY = 3000;
const DROPMENU_BUTTON_BREAKPOINT = 768;

export function SolanaWalletButton({ children }) {
    const { disconnect } = useWallet();
    const { authed, solanaPubKey } = useAuth();
    const { setVisible } = useSolanaWalletModal();
    const [toastMsg, setToastMsg] = useState("");

    const content = useMemo(() => {
        if (children) return children;
        if (!authed) return null;
        return solanaPubKey.slice(0, 4) + "..." + solanaPubKey.slice(-4);
    }, [authed, children, solanaPubKey]);

    const copyAddress = useCallback(async () => {
        if (solanaPubKey) {
            await navigator.clipboard.writeText(solanaPubKey);
            setToastMsg("Address copied!");
        }
    }, [solanaPubKey]);

    const openModal = useCallback(() => {
        setVisible(true);
    }, [setVisible]);

    if (isMobile) {
        return <></>;
    }

    if (!authed) {
        return (
            <button
                onClick={openModal}
                type="button"
                className="inline-flex w-full items-center justify-center rounded-full border border-transparent bg-orange-600 px-5 py-2 font-burbank text-base font-medium text-white shadow-inner hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 md:w-fit"
            >
                Connect Wallet
            </button>
        );
    }

    return (
        <>
            <DropDownButton
                content={content}
                disconnect={disconnect}
                copyAddress={copyAddress}
                openModal={openModal}
            />
            <Toastify
                content={toastMsg}
                duration={TOASTIFY_DELAY}
                setToastMsg={setToastMsg}
            />
        </>
    );
}

function DropDownButton({ content, disconnect, openModal, copyAddress }) {
    const [isDropMenu, setIsDropMenu] = useState(
        document.body.clientWidth >= DROPMENU_BUTTON_BREAKPOINT
    );

    useEffect(() => {
        const handleResize = () => {
            setIsDropMenu(
                document.body.clientWidth >= DROPMENU_BUTTON_BREAKPOINT
            );
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    if (isDropMenu) {
        return (
            <Menu
                as="div"
                className="relative hidden text-left md:inline-block"
            >
                <div>
                    <Menu.Button className="inline-flex items-center justify-center rounded-full border border-transparent bg-orange-600 px-5 py-2 font-burbank text-base font-medium text-white shadow-inner hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2">
                        {content}
                    </Menu.Button>
                </div>
                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Menu.Items className="absolute right-0 mt-2 w-max origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="px-1 py-1 ">
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={copyAddress}
                                        className={`${
                                            active
                                                ? "bg-gray-50 text-gray-800"
                                                : "text-gray-700"
                                        } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                        {active ? (
                                            <DocumentDuplicateIcon
                                                className="mr-2 h-5 w-5 text-orange-500"
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            <DocumentDuplicateIcon
                                                className="mr-2 h-5 w-5 text-orange-400"
                                                aria-hidden="true"
                                            />
                                        )}
                                        Copy address
                                    </button>
                                )}
                            </Menu.Item>
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={disconnect}
                                        className={`${
                                            active
                                                ? "bg-gray-50 text-gray-800"
                                                : "text-gray-700"
                                        } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                        {active ? (
                                            <XCircleIcon
                                                className="mr-2 h-5 w-5 text-orange-500"
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            <XCircleIcon
                                                className="mr-2 h-5 w-5 text-orange-400"
                                                aria-hidden="true"
                                            />
                                        )}
                                        Disconnect
                                    </button>
                                )}
                            </Menu.Item>
                        </div>
                    </Menu.Items>
                </Transition>
            </Menu>
        );
    } else {
        return (
            <button
                onClick={disconnect}
                type="button"
                className="inline-flex w-full items-center justify-center rounded-full border border-transparent bg-orange-600 px-5 py-2 font-burbank text-base font-medium text-white shadow-inner hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 md:hidden"
            >
                {content}
            </button>
        );
    }
}
